import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { SeoHeader } from '@/components/SeoHeader';
import { DocumentSection } from '@/sections/documents/DocumentSection';
import { useTranslation } from 'react-i18next';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import CardFront from '@/assets/icons/heroIcons/Card-front.svg';
import { Button } from '@/components/buttons';
import type { GetStaticProps } from 'next';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useCustomerRequestCreateQuery } from '@/api/skippay/customerRequestCreateQuery';
import Loader from '@/components/Loader';
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn';
import { RefundSentLoggedIn } from '@/sections/overpayRefund/sent/RefundSentLoggedIn';
import { RefundSentLogout } from '@/sections/overpayRefund/sent/RefundSentLogout';
import { ReturnToLimit } from '@/sections/overpayRefund/form/ReturnToLimit';
import { RefundToAccountForm } from '@/sections/overpayRefund/form/RefundToAccountForm';
import { FIELD_BANK_CODE, FIELD_FIRST_PART, FIELD_SECOND_PART, FIELD_THIRD_PART } from '@mallpay/orbis';
interface Props extends IntlProps {}
export default function Page() {
  const $ = _c(39);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["overpayRefund", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const router = useRouter();
  const {
    productType: productTypeFromQuery
  } = router.query;
  usePageView("overpay-refund");
  const [refundSent, setRefundSent] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const customerRequestQuery = useCustomerRequestCreateQuery();
  const {
    isLoggedIn,
    email
  } = useIsLoggedIn();
  const productType = (productTypeFromQuery as string)?.toUpperCase() === "INSTALLMENT" ? "INSTALLMENT" : (productTypeFromQuery as string)?.toUpperCase() === "REVOLVING" ? "REVOLVING" : "BNPL";
  let t1;
  if ($[1] !== customerRequestQuery || $[2] !== email || $[3] !== isLoggedIn || $[4] !== productType || $[5] !== refundSent || $[6] !== submitError || $[7] !== t) {
    t1 = function renderContent() {
      if (customerRequestQuery.isPending) {
        return <Loader hideLabel={true} />;
      }
      if (submitError) {
        return <div className="mb-[160px] flex flex-col items-center justify-center gap-6 rounded-4xl bg-white p-10 lg:p-12"><p className="mb-6 text-sm font-semibold text-red-500 lg:text-base">{t("refundToAccount.error")}</p><Button variant="unstyled" onClick={() => setSubmitError(false)} analyticsParam="tryAgain" testId="tryAgain" className="text-blue-500 underline decoration-1 hover:text-blue-800" align="center">{t("refundToAccount.tryAgain")}</Button></div>;
      }
      if (refundSent) {
        if (isLoggedIn) {
          return <RefundSentLoggedIn />;
        }
        if (!isLoggedIn) {
          return <RefundSentLogout />;
        }
      }
      const handleSubmitAccount = async function handleSubmitAccount(values) {
        ;
        try {
          const accountNumber = [values.bankAccount[FIELD_FIRST_PART], values.bankAccount[FIELD_SECOND_PART], values.bankAccount[FIELD_THIRD_PART]].filter(Boolean).join("-");
          const bankAccountNumber = `${accountNumber}/${values.bankAccount[FIELD_BANK_CODE]}`;
          await customerRequestQuery.mutateAsync({
            email: values.email,
            requestType: "REFUND",
            subject: "Chci po\u017E\xE1dat o vr\xE1cen\xED p\u0159eplatku",
            extraData: {
              productType,
              accountNumber: bankAccountNumber,
              refundType: "BANK_ACCOUNT"
            }
          });
          setRefundSent(true);
        } catch (t2) {
          setSubmitError(true);
        }
      };
      return <div className="mb-38 flex flex-col gap-6 lg:w-192">{isLoggedIn ? <>{productType === "REVOLVING" && <DocumentSection title={t("refundToMaxi.title")} icon={<div className="flex items-center justify-center lg:h-10 lg:w-10"><IconMaxi className="text-blue-500" /></div>} defaultOpen={false} dropdownClassName="overflow-initial"><ReturnToLimit handleSubmit={async () => {
              if (!email) {
                setSubmitError(true);
                return;
              }
              ;
              try {
                await customerRequestQuery.mutateAsync({
                  email,
                  requestType: "REFUND",
                  subject: "Chci po\u017E\xE1dat o vr\xE1cen\xED p\u0159eplatku",
                  extraData: {
                    productType,
                    refundType: "REVOLVING"
                  }
                });
                setRefundSent(true);
              } catch (t3) {
                setSubmitError(true);
              }
            }} disabled={customerRequestQuery.isPending} /></DocumentSection>}<DocumentSection title={t("refundToAccount.title")} icon={<div className="flex items-center justify-center lg:h-10 lg:w-10"><CardFront className="text-blue-500" /></div>} defaultOpen={false} dropdownClassName="overflow-initial"><RefundToAccountForm handleSubmit={handleSubmitAccount} disabled={customerRequestQuery.isPending} isLoggedIn={isLoggedIn} /></DocumentSection></> : <div className="rounded-2xl bg-white px-4 py-6 lg:p-8"><RefundToAccountForm handleSubmit={handleSubmitAccount} disabled={customerRequestQuery.isPending} isLoggedIn={isLoggedIn} /></div>}</div>;
    };
    $[1] = customerRequestQuery;
    $[2] = email;
    $[3] = isLoggedIn;
    $[4] = productType;
    $[5] = refundSent;
    $[6] = submitError;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const renderContent = t1;
  let t2;
  if ($[9] !== isLoggedIn || $[10] !== productType || $[11] !== t) {
    t2 = function getDescription() {
      if (isLoggedIn) {
        if (productType !== "REVOLVING") {
          return t("description.loggedInWithOnlyAccount");
        }
        return t("description.default");
      }
      return t("description.loggedOut");
    };
    $[9] = isLoggedIn;
    $[10] = productType;
    $[11] = t;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const getDescription = t2;
  let t3;
  if ($[13] !== t) {
    t3 = t("seo.title");
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t) {
    t4 = t("seo.description");
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <SeoHeader title={t3} description={t4} nofollow={true} noindex={true} />;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="bgCircle absolute top-1/4 left-0 hidden -translate-x-1/4 -translate-y-1/2 rotate-90 lg:block" />;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t) {
    t7 = t("title");
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t7) {
    t8 = <h1 className="mb-4 text-3xl font-bold lg:text-[64px]">{t7}</h1>;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== getDescription || $[26] !== refundSent) {
    t9 = !refundSent && <p className="text-center text-lg lg:text-[20px]">{getDescription()}</p>;
    $[25] = getDescription;
    $[26] = refundSent;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="mt-38 flex flex-col items-center gap-8">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== renderContent) {
    t11 = renderContent();
    $[31] = renderContent;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11) {
    t12 = <div className="relative z-30 bg-blue-100 px-4">{t6}<div className="relative z-10 mx-auto max-w-[60.5rem]"><div className="flex min-h-[60vh] flex-col items-center justify-center gap-20">{t10}{t11}</div></div></div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t5) {
    t13 = <Layout>{t5}{t12}</Layout>;
    $[36] = t12;
    $[37] = t5;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
export const getStaticProps = (async ctx => {
  return {
    props: {
      ...(await getIntlProps(ctx, ['overpayRefund', 'core']))
    }
  };
}) satisfies GetStaticProps<Props>;